import { forwardRef } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';

import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatNumber, {
    classicFormat,
    percentFormat,
    percentFormatCompact
} from '@/utils/hooks/format/useFormatNumber';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useUser from '@/utils/hooks/swr/useUser';

import { BASE } from '@/types/utils/sizesType';

// Types
import type { UserNumberFormatType } from '@/types/userType';

import type { KPIDataType } from '@/lib/analytics/kpis/kpi.type';
// Libs
import { ORIENTATION_VERTICAL } from '@/lib/gridElements/orientation';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
// Components
import Icon from '@/components/base/Icon';
import { DefaultLoader } from '@/components/base/Kpi/KpiLoader';
import Tooltip from '@/components/base/Tooltip';
import { SocialProfilesAndGroupsPopover } from '@/components/composite/SocialProfiles/SocialProfilesAndGroupsListPopover';
import { Trans } from '@/components/tools/translation';

const MainDataWithTooltip = ({
    value,
    valueFormat
}: {
    value: number;
    valueFormat: KeyNumberKpiType['valueFormat'];
}) => {
    const { user } = useUser({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    });

    const userNumberFormat = user?.settings?.numbers_format ?? 'comma';
    const { formatNumber } = useFormatNumber(userNumberFormat);

    if (typeof value !== 'number') {
        return <span className="text-icoGray-400 dark:text-icoDarkMode-beton">-</span>;
    }

    return (
        <Tooltip
            content={formatNumber(
                value,
                valueFormat === percentFormat
                    ? {
                          ...percentFormat,
                          maximumFractionDigits: 5
                      }
                    : {}
            )}
            placement="top">
            <span>{formatNumber(value, valueFormat)}</span>
        </Tooltip>
    );
};

const DataEvolution = forwardRef<
    HTMLDivElement,
    Pick<KeyNumberKpiType, 'value'> & { userNumberFormat: UserNumberFormatType }
>(({ value, userNumberFormat }, ref) => {
    const { formatNumber } = useFormatNumber(userNumberFormat);

    return (
        <div
            data-testid="kpi-evolution"
            ref={ref}
            className={clsx(
                'inline-flex items-center justify-center gap-1 rounded-2xl bg-white p-2.5 text-xs dark:bg-icoDarkMode-anthracite',
                {
                    'text-icoRed': value.evolution < 0,
                    'text-icoGreen': value.evolution > 0,
                    'text-icoGray-400': value.evolution === 0
                }
            )}>
            <span>
                {value.evolution !== 0
                    ? formatNumber(value.evolution, {
                          ...percentFormatCompact,
                          signDisplay: 'always'
                      })
                    : formatNumber(value.evolution, {
                          ...percentFormatCompact,
                          signDisplay: 'always'
                      }).replace('+', '')}
            </span>
            <Icon
                icon={clsx({
                    'arrow-45-top': value.evolution > 0,
                    'arrow-45-bottom': value.evolution < 0,
                    'arrow-right': value.evolution === 0
                })}
            />
        </div>
    );
});
DataEvolution.displayName = 'DataEvolution';

const DataEvolutionWithTooltip = ({ value }: Pick<KeyNumberKpiType, 'value'>) => {
    const { user } = useUser({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    });

    const userNumbersFormat = user?.settings?.numbers_format;
    const userDateFormat = user?.settings?.date_format;

    const { formatNumber } = useFormatNumber(userNumbersFormat);
    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone);

    if (typeof value?.evolution !== 'number') {
        return null;
    }

    return (
        <div className="flex justify-center lg:justify-start" data-testid="kpi-evolution">
            <Tooltip
                interactive
                content={
                    <Trans
                        i18nKey={clsx({
                            'home:key_numbers.growth_tooltip.down': value.evolution < 0,
                            'home:key_numbers.growth_tooltip.up': value.evolution > 0,
                            'home:key_numbers.growth_tooltip.still': value.evolution === 0
                        })}
                        components={{ br: <br /> }}
                        values={{
                            current: formatNumber(value.value, percentFormat),
                            previous: formatNumber(value.previous, percentFormat),
                            start: formatDateTime(
                                dayjs().subtract(13, 'days').format('YYYY-MM-DD'),
                                userDateFormat
                            ),
                            end: formatDateTime(
                                dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
                                userDateFormat
                            )
                        }}
                    />
                }
                placement="top">
                <DataEvolution value={value} userNumberFormat={user?.settings?.numbers_format} />
            </Tooltip>
        </div>
    );
};

const maxDisplayed = 3;

const KeyNumberSocialProfilesList = () => {
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    });

    const displayed = activatedSocialProfiles?.slice(0, maxDisplayed);
    const inPopover = activatedSocialProfiles?.slice(maxDisplayed);

    return (
        <div className="relative z-2 flex items-center" data-testid="social-profiles-list">
            <div className="z-1 flex flex-row-reverse">
                {displayed
                    ?.reverse()
                    ?.map((sp) => (
                        <Avatar
                            key={sp.id}
                            className="-mx-1"
                            size={BASE}
                            fallback={sp.slug?.[0]}
                            url={sp.external_api_data?.avatar}
                            profileType={sp.social_network_type}
                            title={sp.slug}
                        />
                    ))}
            </div>
            {inPopover?.length ? (
                <SocialProfilesAndGroupsPopover
                    profiles={inPopover}
                    btnClassNames="bg-white dark:bg-icoDarkMode-deepBlack -mx-1"
                />
            ) : null}
        </div>
    );
};

type KeyNumberKpiType = {
    isLoading: boolean;
    title: string;
    value: KPIDataType;
    valueFormat?: Intl.NumberFormatOptions;
    icon: string;
    iconColor: string;
    bgColor: string;
    spDisplay?: boolean;
    evolution?: boolean;
};

const KeyNumberKpi = ({
    isLoading,
    title,
    value,
    valueFormat = classicFormat,
    icon,
    iconColor,
    bgColor,
    spDisplay = false,
    evolution = true
}: KeyNumberKpiType) => {
    if (isLoading) {
        return <DefaultLoader config={{ orientation: ORIENTATION_VERTICAL }} />;
    }

    return (
        <div
            className={clsx(
                'rounded-md border-4 border-white transition duration-200 hover:shadow-icoXL dark:border-icoDarkMode-deepBlack dark:hover:shadow-icoDarkXL',
                bgColor
            )}>
            <div className="flex flex-col items-center space-y-5 px-8 py-12">
                <Icon icon={icon} className={clsx('text-[40px]', iconColor)} />
                <div className="flex flex-col items-center space-y-1">
                    <span className="font-GBold text-5xl leading-none">
                        <MainDataWithTooltip value={value?.value} valueFormat={valueFormat} />
                    </span>
                    <span className="text-center font-GSemiBold text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {title}
                    </span>
                </div>
                {spDisplay ? <KeyNumberSocialProfilesList /> : null}
                {evolution ? <DataEvolutionWithTooltip value={value} /> : null}
            </div>
        </div>
    );
};

export default KeyNumberKpi;
